import { useEffect, useState } from "react";
import { Smartphone, X } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Lightweight floating "Install app" prompt.
 * - Listens for the browser's `beforeinstallprompt` event (Android Chrome,
 *   Edge, Samsung Internet).
 * - Shows a friendly Add-to-Home-Screen hint on iOS Safari (which never fires
 *   beforeinstallprompt — users must use Share → Add to Home Screen).
 * - The user can dismiss; we remember the choice in localStorage for 30 days.
 */
const SNOOZE_KEY = "tc_install_dismissed_at";
const SNOOZE_DAYS = 30;

function isStandalone() {
    return (
        window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone === true
    );
}

function isIos() {
    return /iphone|ipad|ipod/i.test(navigator.userAgent || "");
}

function snoozedRecently() {
    const ts = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    if (!ts) return false;
    return Date.now() - ts < SNOOZE_DAYS * 24 * 3600 * 1000;
}

export default function InstallPrompt() {
    const { t } = useTranslation();
    const [deferred, setDeferred] = useState(null);
    const [show, setShow] = useState(false);
    const [iosHint, setIosHint] = useState(false);

    useEffect(() => {
        if (isStandalone() || snoozedRecently()) return;

        const onBip = (e) => {
            e.preventDefault();
            setDeferred(e);
            setShow(true);
        };
        window.addEventListener("beforeinstallprompt", onBip);

        // iOS Safari doesn't fire beforeinstallprompt — show a hint after 4s
        if (isIos()) {
            const timer = setTimeout(() => {
                setIosHint(true);
                setShow(true);
            }, 4000);
            return () => {
                clearTimeout(timer);
                window.removeEventListener("beforeinstallprompt", onBip);
            };
        }

        return () => window.removeEventListener("beforeinstallprompt", onBip);
    }, []);

    const dismiss = () => {
        localStorage.setItem(SNOOZE_KEY, String(Date.now()));
        setShow(false);
    };

    const install = async () => {
        if (!deferred) return;
        deferred.prompt();
        await deferred.userChoice;
        setDeferred(null);
        setShow(false);
    };

    if (!show) return null;

    return (
        <div
            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-[80] bg-white border-2 border-slate-900 shadow-[6px_6px_0_0_hsl(var(--primary))] p-4"
            data-testid="install-prompt"
        >
            <button
                onClick={dismiss}
                className="absolute top-2 right-2 p-1 text-slate-400 hover:text-slate-900"
                aria-label="Close"
                data-testid="install-prompt-dismiss"
            >
                <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-[hsl(var(--primary))] flex items-center justify-center shrink-0">
                    <Smartphone className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0 pr-4">
                    <div className="font-display font-black uppercase text-sm">
                        {t("install.title")}
                    </div>
                    {iosHint ? (
                        <div className="font-mono text-[11px] text-slate-600 mt-1 leading-relaxed">
                            {t("install.ios_hint")}
                        </div>
                    ) : (
                        <>
                            <div className="font-mono text-[11px] text-slate-600 mt-1">
                                {t("install.body")}
                            </div>
                            <button
                                onClick={install}
                                className="mt-2 bg-slate-900 hover:bg-[hsl(var(--primary))] text-white font-display font-bold uppercase text-xs tracking-wider px-3 py-1.5 transition-colors"
                                data-testid="install-prompt-cta"
                            >
                                {t("install.cta")}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
